import {
  INVEST_PDF_OKRAJ_X,
  INVEST_PDF_SIRKA_OBSAHU,
  zabezpecInvestPdfMiesto,
} from "./investPdfPomocne";

const POCET_MESIACOV_V_ROKU = 12;
const MINIMALNY_POCET_PODKARIET = 2;
const MAXIMALNY_POCET_PODKARIET = 3;
const RENTA_AUTOMAT_ROKY = 20;
const RENTA_AUTOMAT_ZHODNOTENIE = 4;

const FARBA_NADPIS = [120, 102, 67];
const FARBA_TEXT = [45, 45, 45];
const FARBA_JEMNA = [110, 110, 110];
const FARBA_LINKA = [205, 196, 177];
const FARBA_MODRA = [100, 171, 210];
const FARBA_ZLATA = [156, 139, 96];
const FARBA_BIELA = [255, 255, 255];
const FARBA_GRAF_POZADIE = [236, 246, 251];
const FARBA_GRAF_LINKA = [190, 223, 239];

const FORMAT_SUMY = new Intl.NumberFormat("sk-SK", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const FORMAT_PERCENTA = new Intl.NumberFormat("sk-SK", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ROZLOZENIE = Object.freeze({
  padding: 4,
  vyskaNadpisu: 9,
  medzeraKariet: 3,
  vyskaScenara: 54,
  vyskaRenty: 56,
  medzeraZaBlokom: 5,
});

function cislo(hodnota) {
  const vysledok = Number(hodnota);
  return Number.isFinite(vysledok)
    ? vysledok
    : 0;
}

function orez(hodnota, minimum, maximum) {
  return Math.min(
    maximum,
    Math.max(minimum, cislo(hodnota))
  );
}

function formatujSumu(hodnota) {
  return `${
    FORMAT_SUMY.format(
      Math.round(
        Math.max(0, cislo(hodnota))
      )
    )
  } €`;
}

function formatujRentu(hodnota) {
  const renta = Math.max(
    0,
    cislo(hodnota)
  );

  return renta > 0
    ? `${FORMAT_SUMY.format(
        Math.round(renta)
      )} €/mes.`
    : "—";
}

function formatujPercento(hodnota) {
  return FORMAT_PERCENTA.format(
    cislo(hodnota)
  );
}

function jePlatnyScenar(scenar) {
  return (
    Boolean(scenar?.vysledok) &&
    cislo(scenar?.horizont) > 0 &&
    (
      cislo(
        scenar?.jednorazovyVklad
      ) > 0 ||
      cislo(
        scenar?.mesacnyVklad
      ) > 0
    ) &&
    cislo(
      scenar?.vysledok
        ?.konecnaHodnota
    ) > 0
  );
}

function filtrujPlatneScenare(data) {
  if (
    !data?.otvorene ||
    !Array.isArray(data?.polozky)
  ) {
    return [];
  }

  return data.polozky
    .filter(jePlatnyScenar)
    .slice(
      0,
      MAXIMALNY_POCET_PODKARIET
    );
}

function jePlatnaHlavnaRenta(data) {
  return (
    cislo(data?.cielovaSuma) > 0 ||
    cislo(
      data?.vysledok
        ?.konecnaHodnota
    ) > 0
  );
}

function jePlatnaRentaZKapitalu(data) {
  return (
    cislo(data?.rentaKapital) > 0 &&
    cislo(
      data?.rentaKapitalRoky
    ) > 0 &&
    Number.isFinite(
      Number(
        data?.rentaKapitalZhodnotenie
      )
    )
  );
}

function jePlatnaRentaZMesacnejRenty(
  data
) {
  return (
    cislo(data?.rentaMesacne) > 0 &&
    cislo(
      data?.rentaMesacneRoky
    ) > 0 &&
    Number.isFinite(
      Number(
        data?.rentaMesacneZhodnotenie
      )
    )
  );
}

function pocetPlatnychKarietRenty(data) {
  if (!data?.otvorena) {
    return 0;
  }

  return [
    jePlatnaHlavnaRenta(data),
    jePlatnaRentaZKapitalu(data),
    jePlatnaRentaZMesacnejRenty(
      data
    ),
  ].filter(Boolean).length;
}

export function maInvestPdfScenare(
  data
) {
  return (
    filtrujPlatneScenare(data)
      .length >=
    MINIMALNY_POCET_PODKARIET
  );
}

export function maInvestPdfRentu(data) {
  return (
    pocetPlatnychKarietRenty(
      data
    ) >= MINIMALNY_POCET_PODKARIET
  );
}

function vypocitajMesacnuRentu({
  kapital,
  roky,
  zhodnotenie,
}) {
  const kapitalHodnota = orez(
    kapital,
    0,
    9999999
  );

  const pocetMesiacov = Math.round(
    orez(roky, 0, 30) *
      POCET_MESIACOV_V_ROKU
  );

  const rocneZhodnotenie =
    orez(zhodnotenie, 0, 10) / 100;

  if (
    kapitalHodnota <= 0 ||
    pocetMesiacov <= 0
  ) {
    return 0;
  }

  const mesacneZhodnotenie =
    Math.pow(
      1 + rocneZhodnotenie,
      1 / POCET_MESIACOV_V_ROKU
    ) - 1;

  if (mesacneZhodnotenie <= 0) {
    return (
      kapitalHodnota /
      pocetMesiacov
    );
  }

  return (
    (
      kapitalHodnota *
      mesacneZhodnotenie
    ) /
    (
      1 -
      Math.pow(
        1 + mesacneZhodnotenie,
        -pocetMesiacov
      )
    )
  );
}

function vypocitajPotrebnyKapital({
  renta,
  roky,
  zhodnotenie,
}) {
  const rentaHodnota = orez(
    renta,
    0,
    9999
  );

  const pocetMesiacov = Math.round(
    orez(roky, 0, 30) *
      POCET_MESIACOV_V_ROKU
  );

  const rocneZhodnotenie =
    orez(zhodnotenie, 0, 10) / 100;

  if (
    rentaHodnota <= 0 ||
    pocetMesiacov <= 0
  ) {
    return 0;
  }

  const mesacneZhodnotenie =
    Math.pow(
      1 + rocneZhodnotenie,
      1 / POCET_MESIACOV_V_ROKU
    ) - 1;

  if (mesacneZhodnotenie <= 0) {
    return (
      rentaHodnota *
      pocetMesiacov
    );
  }

  return (
    rentaHodnota *
    (
      1 -
      Math.pow(
        1 + mesacneZhodnotenie,
        -pocetMesiacov
      )
    ) /
    mesacneZhodnotenie
  );
}

function vypocitajRealnuRentu({
  renta,
  inflacia,
  rokyDoRenty,
}) {
  const rentaHodnota = Math.max(
    0,
    cislo(renta)
  );

  const inflaciaHodnota = orez(
    inflacia,
    0,
    99
  );

  const roky = Math.max(
    0,
    cislo(rokyDoRenty)
  );

  if (
    rentaHodnota <= 0 ||
    inflaciaHodnota <= 0 ||
    roky <= 0
  ) {
    return 0;
  }

  return (
    rentaHodnota /
    Math.pow(
      1 + inflaciaHodnota / 100,
      roky
    )
  );
}

function vytvorBodySparkline(
  scenar,
  vizualnyStrop
) {
  const pocetBodov = 10;

  const jednorazovo = Math.max(
    0,
    cislo(
      scenar?.jednorazovyVklad
    )
  );

  const mesacne = Math.max(
    0,
    cislo(
      scenar?.mesacnyVklad
    )
  );

  const pocetMesiacov = Math.max(
    1,
    Math.round(
      cislo(scenar?.horizont) *
        POCET_MESIACOV_V_ROKU
    )
  );

  const rocnaMiera =
    Math.max(
      0,
      cislo(scenar?.zhodnotenie)
    ) / 100;

  const mesacnaMiera =
    Math.pow(
      1 + rocnaMiera,
      1 / POCET_MESIACOV_V_ROKU
    ) - 1;

  return Array.from(
    {
      length: pocetBodov,
    },
    (_, index) => {
      const pomer =
        index /
        (pocetBodov - 1);

      const mesiac = Math.round(
        pocetMesiacov * pomer
      );

      const faktor = Math.pow(
        1 + mesacnaMiera,
        mesiac
      );

      const hodnotaJednorazovo =
        jednorazovo * faktor;

      const hodnotaMesacne =
        mesacne > 0
          ? mesacnaMiera > 0
            ? (
                mesacne *
                (
                  faktor - 1
                )
              ) /
              mesacnaMiera
            : mesacne * mesiac
          : 0;

      return Math.min(
        1,
        Math.max(
          0,
          (
            hodnotaJednorazovo +
            hodnotaMesacne
          ) / vizualnyStrop
        )
      );
    }
  );
}

export function pripravInvestPdfScenare(
  data
) {
  const platneScenare =
    filtrujPlatneScenare(data);

  if (
    platneScenare.length <
    MINIMALNY_POCET_PODKARIET
  ) {
    return null;
  }

  const maximalnaHodnota = Math.max(
    1,
    ...platneScenare.map(
      (scenar) =>
        cislo(
          scenar?.vysledok
            ?.konecnaHodnota
        )
    )
  );

  const najlepsiScenarId =
    data?.porovnajScenare
      ? platneScenare.reduce(
          (
            najlepsi,
            scenar
          ) =>
            cislo(
              scenar?.vysledok
                ?.konecnaHodnota
            ) >
            cislo(
              najlepsi?.vysledok
                ?.konecnaHodnota
            )
              ? scenar
              : najlepsi,
          platneScenare[0]
        )?.id || null
      : null;

  return {
    polozky: platneScenare.map(
      (scenar) => {
        const konecnaHodnota =
          Math.max(
            0,
            cislo(
              scenar?.vysledok
                ?.konecnaHodnota
            )
          );

        const vklady = Math.max(
          0,
          cislo(
            scenar?.vysledok
              ?.celkoveVklady
          )
        );

        const vynosZDat = cislo(
          scenar?.vysledok
            ?.celkovyVynos
        );

        const vynos = Math.max(
          0,
          vynosZDat ||
            (
              konecnaHodnota -
              vklady
            )
        );

        const sucet =
          vklady + vynos;

        return {
          id: scenar?.id,
          nazov:
            String(
              scenar?.nazov ||
                "Scenár"
            ).trim() || "Scenár",
          horizont: Math.max(
            0,
            cislo(
              scenar?.horizont
            )
          ),
          zhodnotenie: cislo(
            scenar?.zhodnotenie
          ),
          konecnaHodnota,
          vklady,
          vynos,
          podielVkladov:
            sucet > 0
              ? vklady / sucet
              : 0,
          sparkline:
            vytvorBodySparkline(
              scenar,
              data?.porovnajScenare
                ? maximalnaHodnota
                : Math.max(
                    1,
                    konecnaHodnota
                  )
            ),
          zvyrazneny: Boolean(
            data?.porovnajScenare &&
            scenar?.id ===
              najlepsiScenarId
          ),
        };
      }
    ),
  };
}

export function pripravInvestPdfRentu(
  data
) {
  if (!maInvestPdfRentu(data)) {
    return null;
  }

  const karty = [];

  const inflacia = Math.max(
    0,
    cislo(data?.inflacia)
  );

  if (jePlatnaHlavnaRenta(data)) {
    const cielovaSuma = Math.max(
      0,
      cislo(data?.cielovaSuma)
    );

    const hodnotaInvesticie =
      Math.max(
        0,
        cislo(
          data?.vysledok
            ?.konecnaHodnota
        )
      );

    const bloky = [];

    if (cielovaSuma > 0) {
      const renta =
        vypocitajMesacnuRentu({
          kapital: cielovaSuma,
          roky: RENTA_AUTOMAT_ROKY,
          zhodnotenie:
            RENTA_AUTOMAT_ZHODNOTENIE,
        });

      bloky.push({
        label: "Cieľová suma",
        hodnota:
          formatujSumu(cielovaSuma),
        vysledok:
          formatujRentu(renta),
        realnaHodnota:
          inflacia > 0
            ? formatujRentu(
                vypocitajRealnuRentu({
                  renta,
                  inflacia,
                  rokyDoRenty:
                    RENTA_AUTOMAT_ROKY,
                })
              )
            : "",
      });
    }

    if (hodnotaInvesticie > 0) {
      const renta =
        vypocitajMesacnuRentu({
          kapital:
            hodnotaInvesticie,
          roky: RENTA_AUTOMAT_ROKY,
          zhodnotenie:
            RENTA_AUTOMAT_ZHODNOTENIE,
        });

      bloky.push({
        label:
          "Hodnota investície",
        hodnota: formatujSumu(
          hodnotaInvesticie
        ),
        vysledok:
          formatujRentu(renta),
        realnaHodnota:
          inflacia > 0
            ? formatujRentu(
                vypocitajRealnuRentu({
                  renta,
                  inflacia,
                  rokyDoRenty:
                    RENTA_AUTOMAT_ROKY,
                })
              )
            : "",
      });
    }

    karty.push({
      typ: "hlavna",
      nazov:
        "Z hlavnej investície",
      doplnok: String(
        data?.aktivnyVariantLabel ||
          ""
      ).trim(),
      bloky,
      pata:
        `Dĺžka vyplácania ` +
        `${RENTA_AUTOMAT_ROKY} r ` +
        `pri ${formatujPercento(
          RENTA_AUTOMAT_ZHODNOTENIE
        )} % p.a.`,
    });
  }

  if (jePlatnaRentaZKapitalu(data)) {
    const renta =
      vypocitajMesacnuRentu({
        kapital: data.rentaKapital,
        roky:
          data.rentaKapitalRoky,
        zhodnotenie:
          data.rentaKapitalZhodnotenie,
      });

    karty.push({
      typ: "standardna",
      nazov: "Z kapitálu",
      doplnok: formatujSumu(
        data.rentaKapital
      ),
      hlavnyLabel:
        "Mesačná renta",
      hlavnaHodnota:
        formatujRentu(renta),
      realnyLabel:
        inflacia > 0
          ? `Reálne po ${Math.round(
              cislo(
                data.rentaKapitalRoky
              )
            )} r`
          : "",
      realnaHodnota:
        inflacia > 0
          ? formatujRentu(
              vypocitajRealnuRentu({
                renta,
                inflacia,
                rokyDoRenty:
                  data.rentaKapitalRoky,
              })
            )
          : "",
      pata:
        `Dĺžka vyplácania ` +
        `${Math.round(
          cislo(
            data.rentaKapitalRoky
          )
        )} r pri ` +
        `${formatujPercento(
          data.rentaKapitalZhodnotenie
        )} % p.a.`,
    });
  }

  if (
    jePlatnaRentaZMesacnejRenty(
      data
    )
  ) {
    const potrebnyKapital =
      vypocitajPotrebnyKapital({
        renta: data.rentaMesacne,
        roky:
          data.rentaMesacneRoky,
        zhodnotenie:
          data.rentaMesacneZhodnotenie,
      });

    karty.push({
      typ: "standardna",
      nazov: "Požadovaná renta",
      doplnok: formatujRentu(
        data.rentaMesacne
      ),
      hlavnyLabel:
        "Vložiť jednorazovo",
      hlavnaHodnota:
        formatujSumu(
          potrebnyKapital
        ),
      realnyLabel:
        inflacia > 0
          ? `Reálne po ${Math.round(
              cislo(
                data.rentaMesacneRoky
              )
            )} r`
          : "",
      realnaHodnota:
        inflacia > 0
          ? formatujRentu(
              vypocitajRealnuRentu({
                renta:
                  data.rentaMesacne,
                inflacia,
                rokyDoRenty:
                  data.rentaMesacneRoky,
              })
            )
          : "",
      pata:
        `Dĺžka vyplácania ` +
        `${Math.round(
          cislo(
            data.rentaMesacneRoky
          )
        )} r pri ` +
        `${formatujPercento(
          data.rentaMesacneZhodnotenie
        )} % p.a.`,
    });
  }

  return (
    karty.length >=
    MINIMALNY_POCET_PODKARIET
      ? {
          karty,
        }
      : null
  );
}

function vytvorRozlozenieKariet(
  pocetKariet
) {
  const vnutornaSirka =
    INVEST_PDF_SIRKA_OBSAHU -
    ROZLOZENIE.padding * 2;

  const sirkaKarty =
    (
      vnutornaSirka -
      ROZLOZENIE.medzeraKariet *
        (pocetKariet - 1)
    ) / pocetKariet;

  return {
    sirkaKarty,
  };
}

function zapisZakladBloku(
  pdf,
  stav,
  nazov,
  vyskaKarty,
  pocetKariet
) {
  const vyskaBloku =
    ROZLOZENIE.padding * 2 +
    ROZLOZENIE.vyskaNadpisu +
    vyskaKarty;

  zabezpecInvestPdfMiesto(
    pdf,
    stav,
    vyskaBloku +
      ROZLOZENIE.medzeraZaBlokom
  );

  const x = INVEST_PDF_OKRAJ_X;
  const y = stav.y;

  pdf.setFillColor(...FARBA_BIELA);
  pdf.setDrawColor(...FARBA_LINKA);
  pdf.setLineWidth(0.35);

  pdf.roundedRect(
    x,
    y,
    INVEST_PDF_SIRKA_OBSAHU,
    vyskaBloku,
    3,
    3,
    "FD"
  );

  pdf.setTextColor(...FARBA_NADPIS);
  pdf.setFont("Inter", "bold");
  pdf.setFontSize(9.2);

  pdf.text(
    nazov,
    x + ROZLOZENIE.padding,
    y +
      ROZLOZENIE.padding +
      4.5
  );

  const {
    sirkaKarty,
  } = vytvorRozlozenieKariet(
    pocetKariet
  );

  return {
    x,
    y,
    vyskaBloku,
    kartyY:
      y +
      ROZLOZENIE.padding +
      ROZLOZENIE.vyskaNadpisu,
    prvaKartaX:
      x + ROZLOZENIE.padding,
    sirkaKarty,
  };
}

function zapisObalKarty(
  pdf,
  x,
  y,
  sirka,
  vyska,
  zvyraznena = false
) {
  pdf.setFillColor(...FARBA_BIELA);

  pdf.setDrawColor(
    ...(
      zvyraznena
        ? FARBA_MODRA
        : FARBA_LINKA
    )
  );

  pdf.setLineWidth(
    zvyraznena ? 0.55 : 0.2
  );

  pdf.roundedRect(
    x,
    y,
    sirka,
    vyska,
    2.5,
    2.5,
    "FD"
  );
}

function zapisTextNaStred(
  pdf,
  text,
  x,
  y,
  sirka,
  {
    velkost = 6.5,
    tucne = false,
    farba = FARBA_TEXT,
    maximalnaSirka = sirka - 4,
  } = {}
) {
  pdf.setTextColor(...farba);

  pdf.setFont(
    "Inter",
    tucne ? "bold" : "normal"
  );

  pdf.setFontSize(velkost);

  const riadky =
    pdf.splitTextToSize(
      String(text || ""),
      maximalnaSirka
    );

  pdf.text(
    riadky,
    x + sirka / 2,
    y,
    {
      align: "center",
    }
  );
}

function bodyNaVektory(body) {
  return body
    .slice(1)
    .map(
      (bod, index) => [
        bod.x - body[index].x,
        bod.y - body[index].y,
      ]
    );
}

function zapisSektor(
  pdf,
  stredX,
  stredY,
  polomer,
  zaciatok,
  koniec,
  farba
) {
  const pocetKrokov = Math.max(
    4,
    Math.ceil(
      Math.abs(
        koniec - zaciatok
      ) /
        (Math.PI / 18)
    )
  );

  const body = [
    {
      x: stredX,
      y: stredY,
    },
  ];

  for (
    let index = 0;
    index <= pocetKrokov;
    index += 1
  ) {
    const uhol =
      zaciatok +
      (
        (
          koniec - zaciatok
        ) * index
      ) /
        pocetKrokov;

    body.push({
      x:
        stredX +
        Math.cos(uhol) *
          polomer,
      y:
        stredY +
        Math.sin(uhol) *
          polomer,
    });
  }

  body.push({
    x: stredX,
    y: stredY,
  });

  pdf.setFillColor(...farba);

  pdf.lines(
    bodyNaVektory(body),
    body[0].x,
    body[0].y,
    [1, 1],
    "F",
    true
  );
}

function zapisSparkline(
  pdf,
  body,
  x,
  y,
  sirka,
  vyska
) {
  if (
    !Array.isArray(body) ||
    body.length < 2
  ) {
    return;
  }

  const bodyGrafu = body.map(
    (hodnota, index) => ({
      x:
        x +
        (
          sirka * index
        ) /
          (body.length - 1),
      y:
        y +
        vyska -
        Math.max(
          0,
          Math.min(1, hodnota)
        ) *
          vyska,
    })
  );

  pdf.setDrawColor(...FARBA_MODRA);
  pdf.setLineWidth(0.35);

  pdf.lines(
    bodyNaVektory(bodyGrafu),
    bodyGrafu[0].x,
    bodyGrafu[0].y,
    [1, 1],
    "S",
    false
  );
}

function zapisScenarKartu(
  pdf,
  scenar,
  x,
  y,
  sirka,
  vyska
) {
  zapisObalKarty(
    pdf,
    x,
    y,
    sirka,
    vyska,
    scenar.zvyrazneny
  );

  zapisTextNaStred(
    pdf,
    scenar.nazov,
    x,
    y + 5,
    sirka,
    {
      velkost: 7.2,
      tucne: true,
      farba:
        scenar.zvyrazneny
          ? FARBA_MODRA
          : FARBA_TEXT,
    }
  );

  zapisTextNaStred(
    pdf,
    `${scenar.horizont} r · ` +
      `${formatujPercento(
        scenar.zhodnotenie
      )} % p.a.`,
    x,
    y + 9.2,
    sirka,
    {
      velkost: 5.8,
      farba: FARBA_JEMNA,
    }
  );

  zapisTextNaStred(
    pdf,
    formatujSumu(
      scenar.konecnaHodnota
    ),
    x,
    y + 14,
    sirka,
    {
      velkost: 7.4,
      tucne: true,
    }
  );

  const stredX =
    x + sirka / 2;

  const stredY = y + 29;
  const polomer = 9.5;
  const zaciatok = -Math.PI / 2;

  const hranica =
    zaciatok +
    Math.PI *
      2 *
      scenar.podielVkladov;

  zapisSektor(
    pdf,
    stredX,
    stredY,
    polomer,
    zaciatok,
    hranica,
    FARBA_MODRA
  );

  zapisSektor(
    pdf,
    stredX,
    stredY,
    polomer,
    hranica,
    zaciatok + Math.PI * 2,
    FARBA_ZLATA
  );

  pdf.setFillColor(...FARBA_BIELA);

  pdf.circle(
    stredX,
    stredY,
    polomer * 0.62,
    "F"
  );

  zapisSparkline(
    pdf,
    scenar.sparkline,
    stredX - 6.2,
    stredY - 3.5,
    12.4,
    7
  );

  const metrikaY =
    y + vyska - 8;

  pdf.setFontSize(5.8);
  pdf.setFont("Inter", "normal");
  pdf.setTextColor(...FARBA_JEMNA);

  pdf.text(
    "Vklady",
    x + 4,
    metrikaY
  );

  pdf.text(
    "Výnos",
    x + 4,
    metrikaY + 4
  );

  pdf.setFont("Inter", "bold");
  pdf.setTextColor(...FARBA_TEXT);

  pdf.text(
    formatujSumu(
      scenar.vklady
    ),
    x + sirka - 4,
    metrikaY,
    {
      align: "right",
    }
  );

  pdf.text(
    formatujSumu(
      scenar.vynos
    ),
    x + sirka - 4,
    metrikaY + 4,
    {
      align: "right",
    }
  );

  pdf.setFillColor(...FARBA_MODRA);
  pdf.circle(
    x + 2.4,
    metrikaY - 0.7,
    0.65,
    "F"
  );

  pdf.setFillColor(...FARBA_ZLATA);
  pdf.circle(
    x + 2.4,
    metrikaY + 3.3,
    0.65,
    "F"
  );
}

export function zapisInvestPdfScenare(
  pdf,
  stav,
  scenarePdf
) {
  const polozky = Array.isArray(
    scenarePdf?.polozky
  )
    ? scenarePdf.polozky
    : [];

  if (
    polozky.length <
    MINIMALNY_POCET_PODKARIET
  ) {
    return;
  }

  const blok = zapisZakladBloku(
    pdf,
    stav,
    "Scenáre",
    ROZLOZENIE.vyskaScenara,
    polozky.length
  );

  polozky.forEach(
    (scenar, index) => {
      const x =
        blok.prvaKartaX +
        index *
          (
            blok.sirkaKarty +
            ROZLOZENIE.medzeraKariet
          );

      zapisScenarKartu(
        pdf,
        scenar,
        x,
        blok.kartyY,
        blok.sirkaKarty,
        ROZLOZENIE.vyskaScenara
      );
    }
  );

  stav.y =
    blok.y +
    blok.vyskaBloku +
    ROZLOZENIE.medzeraZaBlokom;
}

function zapisRentaPozadie(
  pdf,
  x,
  y,
  sirka,
  vyska
) {
  const spodok =
    y + vyska - 5;

  const pocet = 7;
  const sirkaStlpca = 2.3;

  const medzera = Math.max(
    1.6,
    (
      sirka -
      12 -
      pocet * sirkaStlpca
    ) /
      (pocet - 1)
  );

  const body = [];

  for (
    let index = 0;
    index < pocet;
    index += 1
  ) {
    const vyskaStlpca =
      4 + index * 2.2;

    const stlpecX =
      x +
      6 +
      index *
        (
          sirkaStlpca +
          medzera
        );

    pdf.setFillColor(
      ...FARBA_GRAF_POZADIE
    );

    pdf.roundedRect(
      stlpecX,
      spodok - vyskaStlpca,
      sirkaStlpca,
      vyskaStlpca,
      0.5,
      0.5,
      "F"
    );

    body.push({
      x:
        stlpecX +
        sirkaStlpca / 2,
      y:
        spodok -
        vyskaStlpca -
        1,
    });
  }

  pdf.setDrawColor(
    ...FARBA_GRAF_LINKA
  );

  pdf.setLineWidth(0.35);

  pdf.lines(
    bodyNaVektory(body),
    body[0].x,
    body[0].y,
    [1, 1],
    "S",
    false
  );
}

function zapisRentaHlavnuKartu(
  pdf,
  karta,
  x,
  y,
  sirka,
  vyska
) {
  const bloky = Array.isArray(
    karta.bloky
  )
    ? karta.bloky
    : [];

  const medzera = 2;

  const sirkaBloku =
    (
      sirka -
      6 -
      medzera *
        (bloky.length - 1)
    ) /
    Math.max(1, bloky.length);

  bloky.forEach(
    (blok, index) => {
      const blokX =
        x +
        3 +
        index *
          (
            sirkaBloku +
            medzera
          );

      zapisTextNaStred(
        pdf,
        blok.label,
        blokX,
        y + 18,
        sirkaBloku,
        {
          velkost: 5.5,
          farba: FARBA_JEMNA,
        }
      );

      zapisTextNaStred(
        pdf,
        blok.hodnota,
        blokX,
        y + 22,
        sirkaBloku,
        {
          velkost: 6.1,
          tucne: true,
        }
      );

      zapisTextNaStred(
        pdf,
        blok.vysledok,
        blokX,
        y + 29,
        sirkaBloku,
        {
          velkost: 7.2,
          tucne: true,
        }
      );

      if (blok.realnaHodnota) {
        zapisTextNaStred(
          pdf,
          "Reálne po 20 r",
          blokX,
          y + 35,
          sirkaBloku,
          {
            velkost: 5.3,
            farba: FARBA_JEMNA,
          }
        );

        zapisTextNaStred(
          pdf,
          blok.realnaHodnota,
          blokX,
          y + 39,
          sirkaBloku,
          {
            velkost: 5.8,
            tucne: true,
          }
        );
      }
    }
  );

  zapisTextNaStred(
    pdf,
    karta.pata,
    x,
    y + vyska - 3.3,
    sirka,
    {
      velkost: 5.3,
      farba: FARBA_JEMNA,
    }
  );
}

function zapisRentaStandardnuKartu(
  pdf,
  karta,
  x,
  y,
  sirka,
  vyska
) {
  zapisTextNaStred(
    pdf,
    karta.hlavnyLabel,
    x,
    y + 21,
    sirka,
    {
      velkost: 6.1,
      farba: FARBA_JEMNA,
    }
  );

  zapisTextNaStred(
    pdf,
    karta.hlavnaHodnota,
    x,
    y + 27,
    sirka,
    {
      velkost: 7.5,
      tucne: true,
    }
  );

  if (
    karta.realnyLabel &&
    karta.realnaHodnota
  ) {
    zapisTextNaStred(
      pdf,
      karta.realnyLabel,
      x,
      y + 35,
      sirka,
      {
        velkost: 5.7,
        farba: FARBA_JEMNA,
      }
    );

    zapisTextNaStred(
      pdf,
      karta.realnaHodnota,
      x,
      y + 39.5,
      sirka,
      {
        velkost: 6.2,
        tucne: true,
      }
    );
  }

  zapisTextNaStred(
    pdf,
    karta.pata,
    x,
    y + vyska - 3.3,
    sirka,
    {
      velkost: 5.3,
      farba: FARBA_JEMNA,
    }
  );
}

function zapisRentaKartu(
  pdf,
  karta,
  x,
  y,
  sirka,
  vyska
) {
  zapisObalKarty(
    pdf,
    x,
    y,
    sirka,
    vyska
  );

  zapisRentaPozadie(
    pdf,
    x,
    y,
    sirka,
    vyska
  );

  zapisTextNaStred(
    pdf,
    karta.nazov,
    x,
    y + 5,
    sirka,
    {
      velkost: 7.1,
      tucne: true,
    }
  );

  if (karta.doplnok) {
    zapisTextNaStred(
      pdf,
      karta.doplnok,
      x,
      y + 9.2,
      sirka,
      {
        velkost: 6.1,
        tucne: true,
      }
    );
  }

  if (karta.typ === "hlavna") {
    zapisRentaHlavnuKartu(
      pdf,
      karta,
      x,
      y,
      sirka,
      vyska
    );
  } else {
    zapisRentaStandardnuKartu(
      pdf,
      karta,
      x,
      y,
      sirka,
      vyska
    );
  }
}

export function zapisInvestPdfRentu(
  pdf,
  stav,
  rentaPdf
) {
  const karty = Array.isArray(
    rentaPdf?.karty
  )
    ? rentaPdf.karty
    : [];

  if (
    karty.length <
    MINIMALNY_POCET_PODKARIET
  ) {
    return;
  }

  const blok = zapisZakladBloku(
    pdf,
    stav,
    "Renta",
    ROZLOZENIE.vyskaRenty,
    karty.length
  );

  karty.forEach(
    (karta, index) => {
      const x =
        blok.prvaKartaX +
        index *
          (
            blok.sirkaKarty +
            ROZLOZENIE.medzeraKariet
          );

      zapisRentaKartu(
        pdf,
        karta,
        x,
        blok.kartyY,
        blok.sirkaKarty,
        ROZLOZENIE.vyskaRenty
      );
    }
  );

  stav.y =
    blok.y +
    blok.vyskaBloku +
    ROZLOZENIE.medzeraZaBlokom;
}